import { Dayjs } from "dayjs";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarMonth, HelpOutline } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import { useContext, useEffect } from "react";
import { SuccessAlertContext } from "App";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { USER_TYPE } from "constants/user-type";
import IncrementalSearchElement from "../components/IncrementalSearchElement";
import useSystemUsageDownload from "../hooks/useSystemUsageDownload";
import downloadHelpPdf from "../api/downloadHelpPdf";

// type Props = {
//   storehouse?: string;
//   storehouseOptions?: Storehouse[];
//   setStorehouse: Dispatch<SetStateAction<string>>;
//   setSupplier: Dispatch<SetStateAction<string>>;
//   setStorehouseSupplier: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>;
// };

// const handleStorehouseChange = (
//   value: string | null,
//   options: Storehouse[] | undefined,
//   setSh: Dispatch<SetStateAction<string>>,
//   setSup: Dispatch<SetStateAction<string>>,
//   setShSup: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>,
// ) => {
//   const list = options ?? [];
//   if (value) {
//     setSh(list.filter((sh) => sh.name === value)[0].name);
//     setSup("");
//     setShSup("倉庫");
//   } else {
//     setSh("");
//     setShSup("");
//   }
// };

/**
 * システム利用状況レポートダウンロードページ
 * returns JSX.Element
 */
function SystemUsagePage() {
  const {showAlert} = useContext(SuccessAlertContext);
  const {
    setTarget,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    companyCd,
    setCompanyCd,
    storeCd,
    setStoreCd,
    isLoading,
    handleDownload,
  } = useSystemUsageDownload(showAlert);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!(userType === "C0001" || userType === "C0002" || userType === "C0003" || userType === "C0006")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: `本部、店舗、得意先、企業ユーザーで再度ログインしてください。`,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        }
      })();
    }, 0);
  }, []);

  return (
    <Box position="relative">
      <Box width="100%" height={32} textAlign="right" position="absolute" top={0}>
        <Button variant="custom-outlined" onClick={async (e) => {
          const url = await downloadHelpPdf(`${getUserType()}/resources/reports/manual_sys_report.pdf`);
          window.open(url, "_blank", "noopener,noreferrer");
        }} startIcon={<HelpOutline color="secondary" />} sx={{height: 24, padding: 1, marginTop: 0.5, marginRight: 1}}>ヘルプ</Button>
      </Box>
      <Box component="header" sx={{ display: "flex", alignItems: "center", px: 2, py: 4 }}>
        <Button
          startIcon={<ArrowBackIosNewIcon />}
          onClick={() => window.history.back()}
          sx={{
            color: "#000",
            textTransform: "none",
            border: "1px solid #ffa500",
            borderRadius: 1,
            mr: 2,
            minWidth: "6rem",
          }}
        >
          戻る
        </Button>
        <Grid container display="flex" flexGrow={1} alignItems="center" justifyContent="center">
          <DownloadIcon sx={{ fontSize: "2rem", color: "text.secondary", mr: 2 }} />
          <Typography
            variant="h5"
            component="h1"
            color="text.secondary"
            sx={{
              textAlign: "center",
              m1: 2,
              fontWeight: 700,
              fontSize: "1.8rem",
            }}
          >
            利用レポート（システム利用状況）ダウンロード
          </Typography>
        </Grid>
      </Box>

      <Container maxWidth="md">
        <Box mb={2} display="flex" alignItems="center" justifyContent="center">
          { (getUserType() === "C0001" || getUserType() === "C0006") &&
            <IncrementalSearchElement value={storeCd} setValue={setStoreCd} setTarget={setTarget}/>
          }
          {/* 修正箇所 2025/05/20 */}
          {/* <TextField
            fullWidth
            label=" 店舗名"
            placeholder="店舗名を入力"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            sx={{ bgcolor: "#fff", width: 400 }}
          /> */}
        </Box>

        <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, mb: 2, maxWidth: 800, mx: "auto" }}>
          <Grid container justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
              期間を指定してCSVレポートを取得します
            </Typography>
          </Grid>
          {/* Date Area */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <Grid container alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Grid item px={0}>
                <DatePicker
                  label="開始日"
                  format="YYYY/MM/DD"
                  value={dateFrom}
                  maxDate={dateTo ?? undefined}
                  onChange={(value: Dayjs | null) => {
                    setDateFrom(value);
                    if (dateTo && value && value.isAfter(dateTo, "day")) {
                      setDateTo(null);
                    }
                  }}
                  slots={{
                    openPickerIcon: CalendarMonth,
                  }}
                />
              </Grid>
              <Grid item px={0}>
                <Typography> ～ </Typography>
              </Grid>
              <Grid item px={0}>
                <DatePicker
                  label="終了日"
                  format="YYYY/MM/DD"
                  value={dateTo}
                  minDate={dateFrom ?? undefined}
                  onChange={(value: Dayjs | null) => setDateTo(value)}
                  slots={{
                    openPickerIcon: CalendarMonth,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="left" justifyContent="left" px={15} spacing={-5}>
              <Typography variant="caption" color="text.secondary">
                ※ 最大2年前までダウンロード可能
              </Typography>
            </Grid>
          </LocalizationProvider>

          <Grid
            item
            xs={12}
            sx={{
              textAlign: "right",
              mt: "2",
            }}
          >
            <Button
              variant="contained"
              disabled={isLoading}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                bgcolor: "#ffa500",
                color: "#000000",
                ":hover": { bgcolor: "#e07b00" },
                textTransform: "none",
              }}
            >
              {isLoading ? "ダウンロード中" : "ダウンロード"}
            </Button>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default SystemUsagePage;
